import React, { useState, useEffect, useRef } from "react";
import {
  SignalIcon,
  FilmIcon,
  PhotoIcon,
  TvIcon,
} from "@heroicons/react/24/outline";
import { useNowPlayingSubscription } from "../hooks/useNowPlayingSubscription";
import { useTenantAssets } from "../hooks/useTenantAssets";
import { getFullMediaUrl } from "../utils/mediaUtils";

interface NowPlayingPreviewProps {
  screenId?: string;
  screenStatus?: boolean;
  isOpen: boolean;
}

export default function NowPlayingPreview({
  screenId,
  screenStatus,
  isOpen,
}: NowPlayingPreviewProps) {
  // Subscribe as soon as the modal is open — deliberately NOT gated on
  // screenStatus. Gating on status delayed the subscription until the
  // device reported "on", which happens right as it starts loading; the
  // first creative could then start playing before the WebSocket
  // subscription was wired up and get missed. Starting at modal-open
  // gives the subscription plenty of lead time. screenStatus still
  // controls what is *displayed* (the "Screen is offline" state below).
  const nowPlaying = useNowPlayingSubscription(screenId, isOpen);
  const { assets: backgroundAssets } = useTenantAssets(undefined, "background");
  const backgroundAsset = backgroundAssets[0];
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Progress timer
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (!nowPlaying?.startedAt || !nowPlaying?.duration) {
      setElapsed(0);
      return;
    }

    const startTime = new Date(nowPlaying.startedAt).getTime();

    const tick = () => {
      const now = Date.now();
      const elapsedSec = Math.floor((now - startTime) / 1000);
      // Loop the elapsed time when creative repeats (instead of freezing at 100%)
      setElapsed(
        nowPlaying.duration! > 0
          ? elapsedSec % nowPlaying.duration!
          : elapsedSec,
      );
    };

    tick();
    intervalRef.current = setInterval(tick, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [nowPlaying?.startedAt, nowPlaying?.duration]);

  // Screen is offline
  if (!screenStatus) {
    return (
      <div className="border rounded-xl p-4 bg-gray-50 shadow-sm mt-4">
        <div className="flex items-center gap-2 mb-3">
          <TvIcon className="w-5 h-5 text-gray-400" />
          <span className="font-medium text-gray-500">Live Preview</span>
        </div>
        <div className="flex flex-col items-center justify-center py-6 text-gray-400">
          <TvIcon className="w-10 h-10 mb-2" />
          <p className="text-sm">Screen is offline</p>
        </div>
      </div>
    );
  }

  // No creative playing — mirror the device's idle state, which shows the
  // tenant's background asset. Fall back to text if no background is configured.
  if (!nowPlaying?.creativeId) {
    return (
      <div className="border rounded-xl p-4 bg-gray-50 shadow-sm mt-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <TvIcon className="w-5 h-5 text-gray-600" />
            <span className="font-medium text-gray-700">Live Preview</span>
          </div>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
            IDLE
          </span>
        </div>
        {backgroundAsset?.fileUrl ? (
          <>
            <div className="rounded-lg overflow-hidden bg-black h-40 mb-3">
              <img
                src={getFullMediaUrl(backgroundAsset.fileUrl)}
                alt="Tenant background"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="text-xs text-gray-500 text-center">
              No schedule running
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-6 text-gray-400">
            <SignalIcon className="w-10 h-10 mb-2" />
            <p className="text-sm">No content playing</p>
          </div>
        )}
      </div>
    );
  }

  const progress =
    nowPlaying.duration && nowPlaying.duration > 0
      ? Math.min((elapsed / nowPlaying.duration) * 100, 100)
      : 0;

  const isVideo = nowPlaying.creativeType === "video";

  return (
    <div className="border rounded-xl p-4 bg-gray-50 shadow-sm mt-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <TvIcon className="w-5 h-5 text-gray-600" />
          <span className="font-medium text-gray-700">Live Preview</span>
        </div>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
          LIVE
        </span>
      </div>

      {/* Media Preview */}
      <div className="rounded-lg overflow-hidden bg-black h-40 mb-3">
        {nowPlaying.fileUrl ? (
          isVideo ? (
            <video
              key={`${nowPlaying.fileUrl}-${nowPlaying.startedAt}`}
              src={getFullMediaUrl(nowPlaying.fileUrl)}
              className="w-full h-full object-contain"
              autoPlay
              muted
              playsInline
            />
          ) : (
            <img
              src={getFullMediaUrl(nowPlaying.fileUrl)}
              alt={nowPlaying.creativeName || "Creative"}
              className="w-full h-full object-contain"
            />
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-500">
            {isVideo ? (
              <FilmIcon className="w-12 h-12" />
            ) : (
              <PhotoIcon className="w-12 h-12" />
            )}
          </div>
        )}
      </div>

      {/* Info Bar */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          {isVideo ? (
            <FilmIcon className="w-4 h-4 text-blue-500 flex-shrink-0" />
          ) : (
            <PhotoIcon className="w-4 h-4 text-purple-500 flex-shrink-0" />
          )}
          <span className="text-sm font-medium text-gray-700 truncate">
            {nowPlaying.creativeName || "Unknown"}
          </span>
          <span
            className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
              isVideo
                ? "bg-blue-100 text-blue-600"
                : "bg-purple-100 text-purple-600"
            }`}
          >
            {nowPlaying.creativeType || "media"}
          </span>
        </div>
      </div>
    </div>
  );
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
